import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { trainingHeatmap } from "../components/Heatmap.js";
import { spinner, errorState, emptyState } from "../components/ui.js";
import { muscleName } from "../store.js";
import { fmtDurationLong, fmtVolume, fmtDistance, fmtPace, parseDate } from "../lib/format.js";
import { volumeBreakdown, openVolumeInfo } from "../components/volumeCoverage.js";
import { weeklyReviewCard } from "../components/weeklyReview.js";
import { sessionTrend } from "../components/sessionTrend.js";

export default function History() {
  const root = h("div", { class: "screen progress-screen" });
  const workoutFilter = new URLSearchParams(location.search).get("workout");
  load();

  async function load() {
    setChildren(root, progressHeader(), progressShortcuts(), spinner());
    try {
      const sessions = await api.sessionHistory(workoutFilter ? `?workout_id=${workoutFilter}&limit=200` : "?limit=200");
      // Muscle analytics only make sense for the whole history, not a filtered view.
      const [muscleVol, muscleSets] = workoutFilter
        ? [null, null]
        : await Promise.all([api.muscleVolume(8).catch(() => null), api.muscleSets(4).catch(() => null)]);
      render(sessions, muscleVol, muscleSets, workoutFilter ? null : await api.weeklyReview().catch(()=>null));
    } catch (e) {
      setChildren(root, progressHeader(), progressShortcuts(), errorState(e.message, load));
    }
  }

  function render(sessions, muscleVol, muscleSets, review) {
    if (!sessions.length) {
      setChildren(root, progressHeader(), weeklyReviewCard(review,{compact:true}), progressShortcuts(), emptyState({ icon: "history", title: "No workouts logged yet", message: "Finish a session and it'll appear here.", action: h("button", { class: "btn btn-primary", onClick: () => navigate("/start") }, "Start a workout") }));
      return;
    }

    let view = workoutFilter ? "workouts" : "overview";
    const content = h("div");
    const choices = h("div", { class: "tabs progress-tabs", "aria-label": "Progress view" });
    const renderView = () => {
      for (const button of choices.querySelectorAll("button")) {
        const active = button.dataset.view === view;
        button.classList.toggle("active", active);
        button.setAttribute("aria-pressed", String(active));
      }
      setChildren(content, view === "workouts"
        ? h("section", { "aria-label": "Training log", class: "training-log mt" }, ...sessions.map(card))
        : view === "muscles" ? h("div", {class:"mt"}, muscleSetsCard(muscleSets), muscleVolumeCard(muscleVol))
        : h("div", { class: "progress-overview" },
            weeklyReviewCard(review,{compact:true}),
            h("section", { class: "progress-trend" }, h("div", { class: "section-title" }, "Your training rhythm"), sessionTrend(sessions)),
            h("section", { class: "progress-tools" }, h("div", { class: "section-title" }, "Body & milestones"), progressShortcuts()),
            workoutFilter ? null : h("section", { class: "progress-consistency" },
              h("div", { class: "section-title row-between" }, "Consistency", h("span", { class: "dim" }, "Last 18 weeks")),
              h("div", { class: "card" }, trainingHeatmap(sessions)))));
    };
    for (const [key, label] of [["overview", "Overview"], ["workouts", "Workouts"], ...(!workoutFilter ? [["muscles", "Muscles"]] : [])]) {
      choices.appendChild(h("button", { dataset: { view: key }, onClick: () => { view = key; renderView(); } }, label));
    }
    renderView();

    setChildren(root,
      progressHeader("See what your effort adds up to."),
      h("div", { class: "progress-summary" },
        summary(String(sessions.length), `${sessions.length === 200 ? "Latest " : ""}${workoutFilter ? "matching " : ""}workouts`),
        summary(fmtDurationLong(sessions.reduce((total, s) => total + (s.duration_seconds || 0), 0)), "Time trained"),
        summary(sessions.reduce((total, s) => total + (s.set_count || 0), 0).toLocaleString(), "Sets logged")),
      choices, content
    );
  }

  function summary(value, label) {
    return h("div", null, h("strong", null, value), h("span", null, label));
  }

  // Weekly hard sets per muscle vs personalised MEV/MAV/MRV hypertrophy landmarks — the
  // actionable muscle-growth signal that raw kg tonnage can't express. The bands are
  // per-muscle (see lib/landmarks.js) and every abbreviation is explained in the "(i)"
  // modal, so the card teaches the concepts as it reports them.
  function progressHeader(sub = "Training history and trends") {
    return topbar("Progress", { sub });
  }

  function progressShortcuts() {
    return h("div", { class: "shortcut-row", "aria-label": "Progress tools" },
      shortcut("weight", "Weight", "/weight"),
      shortcut("ruler", "Measurements", "/measurements"),
      shortcut("camera", "Photos", "/progress/photos"),
      shortcut("trophy", "Achievements", "/achievements"),
      shortcut("chart", "Standards", "/standards"),
      shortcut("calendar", "Weekly review", "/week"),
      shortcut("calendar", "Your year", "/year")
    );
  }

  function shortcut(iconName, label, path) {
    return h("button", { class: "shortcut-btn", onClick: () => navigate(path) }, icon(iconName, { size: 16 }), label);
  }

  function muscleSetsCard(muscleSets) {
    const list = muscleSets?.muscles?.filter((m) => m.sets > 0) || [];
    if (list.length < 2) return null;
    const items = list.map((m) => ({ name: muscleName(m.muscle_id) || "—", perWeek: m.per_week }));

    return h("div", null,
      h("div", { class: "section-title row-between", style: { marginTop: "8px" } },
        h("span", null, "Weekly hard sets per muscle"),
        h("button", { class: "link-btn dim", style: { fontSize: ".82rem", display: "inline-flex", alignItems: "center", gap: "4px" }, onClick: openVolumeInfo },
          icon("info", { size: 14 }), "What's this?")
      ),
      h("div", { class: "card" },
        h("p", { class: "dim", style: { fontSize: ".76rem", margin: "0 0 6px", lineHeight: "1.45" } },
          `Averaged over the last ${muscleSets.weeks} weeks. Only sets taken near failure (RPE 7+) count; the coloured band shows each muscle's growth zone (MEV → MAV → MRV).`),
        volumeBreakdown(items)
      )
    );
  }

  // Where training volume actually went, by primary muscle, over the last 8 weeks.
  function muscleVolumeCard(muscleVol) {
    const list = muscleVol?.muscles?.filter((m) => m.volume_kg > 0) || [];
    if (list.length < 2) return null;
    const max = list[0].volume_kg; // server returns descending
    return h("div", null,
      h("div", { class: "section-title", style: { marginTop: "8px" } }, `Volume by muscle · last ${muscleVol.weeks} weeks`),
      h("div", { class: "card" },
        ...list.map((m) =>
          h("div", { class: "mv-row", style: { padding: "6px 0" } },
            h("div", { class: "row-between", style: { fontSize: ".84rem", marginBottom: "4px" } },
              h("span", { style: { fontWeight: 600 } }, muscleName(m.muscle_id) || "—"),
              h("span", { class: "dim" }, `${fmtVolume(m.volume_kg)} · ${m.set_count} sets`)
            ),
            h("div", { class: "progress" }, h("i", { style: { width: `${Math.max(3, (m.volume_kg / max) * 100)}%` } }))
          )
        )
      )
    );
  }

  function card(s) {
    const d = parseDate(s.started_at);
    return h(
      "div",
      { class: "card card-tap", style: { marginBottom: "10px" }, onClick: () => navigate(`/history/session/${s.id}`) },
      h(
        "div",
        { class: "history-item" },
        h("div", { class: "history-date" },
          h("div", { class: "d" }, d ? d.getDate() : "?"),
          h("div", { class: "m" }, d ? d.toLocaleString(undefined, { month: "short" }) : ""),
          d && d.getFullYear() !== new Date().getFullYear() ? h("div", { class: "y" }, String(d.getFullYear())) : null
        ),
        h(
          "div",
          { class: "grow" },
          h("div", { style: { fontWeight: 700, fontSize: "1.05rem" } }, s.name || "Workout"),
          h(
            "div",
            { class: "chip-row mt", style: { gap: "6px" } },
            ...sessionChips(s)
          )
        ),
        icon("forward", { size: 18, className: "icon muted" })
      )
    );
  }

  function chip(ic, label) {
    return h("span", { class: "chip", style: { gap: "4px" } }, ic, label);
  }

  // Summary chips. App/manual sessions render EXACTLY as before (duration · ex · volume) — the
  // distance/HR/provenance chips only appear when that data is actually present, so a user who
  // never logs cardio or connects a band sees an unchanged card. Band imports (volume always 0,
  // meaningless for cardio) swap the volume chip for a "Health Connect" provenance chip.
  function sessionChips(s) {
    const chips = [
      chip(icon("timer", { size: 13 }), fmtDurationLong(s.duration_seconds || 0)),
      chip(icon("dumbbell", { size: 13 }), `${s.exercise_count} ex`),
    ];
    if (s.distance_m) {
      const pace = fmtPace(s.duration_seconds, s.distance_m);
      chips.push(chip(icon("ruler", { size: 13 }), fmtDistance(s.distance_m) + (pace ? ` · ${pace}` : "")));
    }
    if (s.avg_hr) chips.push(chip(icon("heart", { size: 13 }), `${s.avg_hr} bpm`));
    if (s.source === "health_connect") chips.push(chip(icon("pulse", { size: 13 }), "Health Connect"));
    else chips.push(chip(icon("weight", { size: 13 }), fmtVolume(s.volume_kg)));
    return chips;
  }

  return root;
}
